import { Stack, useFocusEffect, usePathname, router } from 'expo-router'
import { Platform, View } from 'react-native'
import { withCardGate } from '@/src/experiences/card-accounts/withCardGate'
import { Loading } from '@/src/common/components/Loading'
import { useCallback, useEffect, useRef } from 'react'
import { Routes } from '@/src/common/enums/routes'
import { mesaColorNames } from '@/src/common/theme'
import { cardApplicationEntryBackgroundColor } from '@/src/app/main/(tabs)/card/cardBenefits'
import { useEvent } from 'tamagui'

function isViewingCardBenefits(pathname: ReturnType<typeof usePathname>) {
  return pathname === Routes.CardBenefits.aliases[1]
}

export default function CardLayout() {
  return (
    <View style={{ flex: 1, flexDirection: 'column', justifyContent: 'center' }}>
      <LayoutNav />
    </View>
  )
}

const LayoutNav = withCardGate(function LayoutNav({
  applicationStatusInitialized,
  refetchApplicationStatus: unmemoizedRefetchApplicationStatus,
  firstCardFetchFinished,
  showCardManagement,
  newestApplication,
}) {
  const refetchApplicationStatus = useCallback(() => {
    try {
      void unmemoizedRefetchApplicationStatus()
    } catch (error) {
      if (!(error instanceof Error)) throw error
      switch (error.message) {
        // seems like rtkq doesn't have their reactivity properly setup, because we are definitely guarding against `isUninitialized || isLoading` before refetching...
        case 'Cannot refetch a query that has not been started yet.':
        case 'Minified Redux Toolkit error #38; visit https://redux-toolkit.js.org/Errors?code=38 for the full message or use the non-minified dev environment for full errors.':
        case 'Minified Redux Toolkit error #38; visit https://redux-toolkit.js.org/Errors?code=38 for the full message or use the non-minified dev environment for full errors. ':
          break
        default:
          throw error
      }
    }
  }, [unmemoizedRefetchApplicationStatus])

  useFocusEffect(refetchApplicationStatus)

  const pathname = usePathname()
  const pathnameRef = useRef(pathname)
  pathnameRef.current = pathname

  const viewingCardBenefits = isViewingCardBenefits(pathname)

  useEffect(() => {
    if (viewingCardBenefits) {
      refetchApplicationStatus()
    }
  }, [viewingCardBenefits, refetchApplicationStatus])

  /**
   * the user can end up here if they used the navigation's "back" function after applying and
   * getting approved for a card application in a single session. they can also end up here via
   * deeplinking. when this happens, instead of showing the card benefits page, show the user the
   * card approved page (it's ok if they don't immediately see the card management page - they'll
   * be able to see it any time after the card approved page, but it's not ok if they don't see
   * the card approved page because they need to be able to review the APR, credit limit, etc.)
   *
   * ---
   * in the new platform-based architecture, we introduce a new `APPROVAL_ACKED` status, which comes after a user acknowledges an `ISSUED` card application.
   * because the APPROVAL_ACKED status does not exist in the legacy architecture, to avoid regressions in behavior, we need to ensure that we only redirect to the approved page if the newest application is `ISSUED`:
   * - legacy-ISSUED redirects to /card-application/applicationApproved
   * - platform-ISSUED redirects to /card-application/applicationApproved
   * - platform-APPROVAL_ACKED no-ops in this function (gating to card management home, if necessary, happens from whichever route the user is navigating to)
   */
  const replaceToApplicationApproved = useEvent(
    () =>
      newestApplication?.status === 'ISSUED' && router.replace(Routes.ApplicationApproved.route),
  )

  useEffect(() => {
    setTimeout(
      () =>
        applicationStatusInitialized &&
        showCardManagement &&
        isViewingCardBenefits(pathnameRef.current) &&
        replaceToApplicationApproved(),
    )
  }, [
    viewingCardBenefits,
    showCardManagement,
    applicationStatusInitialized,
    replaceToApplicationApproved,
  ])

  if (!firstCardFetchFinished || !applicationStatusInitialized) {
    return <Loading />
  }

  if (showCardManagement) {
    return (
      <Stack
        screenOptions={{
          headerShown: false,
          statusBarBackgroundColor: mesaColorNames['Mesa - Brand/Secondary #223843'],
        }}
      >
        <Stack.Screen name="card-management" />
      </Stack>
    )
  }

  return (
    <Stack screenOptions={{ headerShown: false }}>
      <Stack.Screen
        name="cardBenefits"
        options={{
          statusBarStyle: Platform.OS === 'android' ? 'dark' : undefined,
          contentStyle: { backgroundColor: cardApplicationEntryBackgroundColor },
        }}
      />
      {/* For the race condition between the card application processing + approved
      and user navigating to card benefits page prior to approval */}
      <Stack.Screen name="card-management" />
    </Stack>
  )
})
